import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function CircuitPowerCalculator() {
  const [voltage, setVoltage] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Circuit Power Calculator", url: "https://calclab.app/calculator/circuit-power" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Circuit Power Calculator",
      "Calculate electrical power in circuits using P=VI, P=V²/R, or P=I²R formulas. Free online electronics calculator.",
      "https://calclab.app/calculator/circuit-power",
      lang
    )
  ]);
  const [current, setCurrent] = useState("");
  const [resistance, setResistance] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateFromVI = () => {
    const v = parseFloat(voltage);
    const i = parseFloat(current);

    if (isNaN(v) || isNaN(i)) {
      toast.error("Please enter valid values");
      return;
    }

    const power = v * i;
    setResult(power);
    toast.success("Calculation complete!");
  };

  const calculateFromVR = () => {
    const v = parseFloat(voltage);
    const r = parseFloat(resistance);

    if (isNaN(v) || isNaN(r) || r === 0) {
      toast.error("Please enter valid values (resistance must be non-zero)");
      return;
    }

    const power = (v * v) / r;
    setResult(power);
    toast.success("Calculation complete!");
  };

  const calculateFromIR = () => {
    const i = parseFloat(current);
    const r = parseFloat(resistance);

    if (isNaN(i) || isNaN(r)) {
      toast.error("Please enter valid values");
      return;
    }

    const power = i * i * r;
    setResult(power);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setVoltage("");
    setCurrent("");
    setResistance("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Circuit Power Calculator" calculatorPath="/calculator/circuit-power">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Circuit Power Calculator</h1>
          <p className="text-muted-foreground">
            Calculate electrical power in circuits
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Power Formulas</CardTitle>
            <CardDescription>
              P = VI | P = V²/R | P = I²R
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="vi" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="vi">V & I</TabsTrigger>
                <TabsTrigger value="vr">V & R</TabsTrigger>
                <TabsTrigger value="ir">I & R</TabsTrigger>
              </TabsList>

              <TabsContent value="vi" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="voltageVI">Voltage (V)</Label>
                    <Input
                      id="voltageVI"
                      type="number"
                      step="0.01"
                      placeholder="Enter voltage"
                      value={voltage}
                      onChange={(e) => setVoltage(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currentVI">Current (A)</Label>
                    <Input
                      id="currentVI"
                      type="number"
                      step="0.01"
                      placeholder="Enter current"
                      value={current}
                      onChange={(e) => setCurrent(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromVI} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="vr" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="voltageVR">Voltage (V)</Label>
                    <Input
                      id="voltageVR"
                      type="number"
                      step="0.01"
                      placeholder="Enter voltage"
                      value={voltage}
                      onChange={(e) => setVoltage(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="resistanceVR">Resistance (Ω)</Label>
                    <Input
                      id="resistanceVR"
                      type="number"
                      step="0.01"
                      placeholder="Enter resistance"
                      value={resistance}
                      onChange={(e) => setResistance(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromVR} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="ir" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentIR">Current (A)</Label>
                    <Input
                      id="currentIR"
                      type="number"
                      step="0.01"
                      placeholder="Enter current"
                      value={current}
                      onChange={(e) => setCurrent(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="resistanceIR">Resistance (Ω)</Label>
                    <Input
                      id="resistanceIR"
                      type="number"
                      step="0.01"
                      placeholder="Enter resistance"
                      value={resistance}
                      onChange={(e) => setResistance(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromIR} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} W
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Power (Watts)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["circuit-power"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>Choose the method based on known values:</p>
                <p>• P = VI (when voltage and current are known)</p>
                <p>• P = V²/R (when voltage and resistance are known)</p>
                <p>• P = I²R (when current and resistance are known)</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 12 V with 2 A = 24 W power
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
